/* eslint-disable react/jsx-no-target-blank */
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { getCollectionDownloadUrl } from 'src/api/functions/collectionsApi';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import { useCollectionQuery, useCollectionSearchQuery } from 'src/api/queries/collectionsQueries';
import { queryNames } from 'src/api/utils/queryNames';
import { useAlerts } from 'src/common/AlertManager';
import {
  Copy,
  FileArrowDown,
  Pencil,
  ShareNetwork,
  Trash,
  AssetGetLink,
  CaretLeft,
  CaretRight,
  X,
  Checks,
  ArrowsClockwise,
} from 'src/common/icons';
import { ReprocessModal, MAX_REPROCESS_ASSETS } from 'src/components/ReprocessFlow';

import useRoleManager from 'src/hooks/useRoleManager';

import {
  BatchActions,
  Button,
  LayoutView,
  LayoutViewSwitcher,
  ListingCard,
  ListingRow,
  PageTitle,
  AddAssetsToShare,
  CampaignLinksModal,
} from 'src/components';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import { getThumbnailUrl, thumbnailVersion } from 'src/utils/previews';
import EditCollectionModal from './components/EditCollectionModal';
import { SendForReviewModal } from 'src/pages/Workflows';
import { useCollectionFilters, COLLECTION_PAGE_SIZE } from './useCollectionFilters';
import { useAuthUser } from 'react-auth-kit';
import moment from 'moment';

import styles from './styles.module.scss';
import ConfirmDuplicateCollectionModal from 'src/components/ConfirmDuplicateCollectionModal';
import BoardingTour from 'src/components/BoardingTour';

type CollectionParams = {
  id: string;
};

enum PAGE_ACTIONS {
  REMOVE_FROM_COLLECTION = 'removeFromCollection',
  SHARE_SELECTED = 'shareSelected',
  CAMPAIGN_LINKS_SELECTED = 'campaignLinksSelected',
  REPROCESS_SELECTED = 'reprocessSelected',
}

const Collection = () => {
  const auth = useAuthUser();
  const userEmail = auth() ? auth()!.userEmail : '';

  const { id } = useParams<CollectionParams>();
  const collectionId = parseInt(id);
  const { isUser, isAdmin, isManager } = useRoleManager();
  const canReprocess = isAdmin || isManager;
  const deliveryInsightsEnabled = useDeliveryInsightsEnabled();

  const queryClient = useQueryClient();

  // Collection metadata (name, description, owner, group access, full asset list).
  const { data: collection } = useCollectionQuery(collectionId);

  // URL-driven, schema-aware filters + pagination for the asset listing.
  const { query, page, hasFilters, chips, setPage, removeFilter, clearFilters } = useCollectionFilters();
  const { data: searchData, isLoading: isSearchLoading } = useCollectionSearchQuery(collectionId, query);

  const displayedAssets: string[] = searchData?.data?.matching_assets ?? [];
  const extendedResult = searchData?.data?.extended_result ?? {};
  const totalMatching = searchData?.data?.total ?? 0;
  const viewUrl = searchData?.data?.view_url ?? '';
  const totalPages = Math.max(1, Math.ceil(totalMatching / COLLECTION_PAGE_SIZE));

  const accessToken = useAccessToken();
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const { showAlert, showSuccess } = useAlerts();
  const { showModal } = useModal();

  const { getErrorMessage } = useErrorMessages();

  const [selectedAssets, updateSelectedAssets] = useAtom(selectedAssetsAtom);

  const patchCollectionMutation = useCollectionMutation(collectionId, {
    onSuccess: () => {
      // Refetch both the metadata and the filtered listing for this collection.
      queryClient.invalidateQueries([queryNames.collection, collectionId]);
      updateSelectedAssets([]);
      showSuccess(`Successfully removed assets from collection`);
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const handleOnAssetSelectChange = (assetId: string) => {
    const newSelectedAssets = selectedAssets.includes(assetId)
      ? selectedAssets.filter((id) => id !== assetId)
      : [...selectedAssets, assetId];
    updateSelectedAssets(newSelectedAssets);
  };

  const handleDownloadCollection = async () => {
    if (isPreparingDownload) return;

    setIsPreparingDownload(true);
    const url: any = await getCollectionDownloadUrl(collectionId, accessToken);

    if (url.status === 404) {
      showAlert(`Download not available at the moment. Please try again later.`);
      setIsPreparingDownload(false);
      return;
    }

    window.open(url);
    setIsPreparingDownload(false);
  };

  const showEditCollectionModal = () => showModal(<EditCollectionModal collection={collection.data} />);

  // One entry point for both review lanes. Replaces the old Notify (internal
  // task) and Share All (external link) pair: the user answers who is
  // reviewing, and the wizard picks the mechanism.
  const handleSendForReview = () => {
    showModal(
      <SendForReviewModal
        collection={{
          id: collectionId,
          name: collection?.data?.name ?? 'Collection',
          groupAccess: collection?.data?.group_access ?? false,
          assetIds: collection?.data?.assets ?? [],
        }}
        initialQuery={query}
      />,
    );
  };

  const handleCopyLink = async () => {
    if (!viewUrl) return;
    try {
      await navigator.clipboard.writeText(viewUrl);
      showSuccess('Link copied to clipboard');
    } catch {
      showAlert('Could not copy the link');
    }
  };

  const handleSelectAllAssets = (selected: boolean) => {
    updateSelectedAssets(selected ? displayedAssets : []);
  };

  const handleRemoveSelectedAssets = () => {
    patchCollectionMutation.mutateAsync({
      deletions: selectedAssets,
    });
  };

  const handleShareSelectedAssets = () => {
    showModal(<AddAssetsToShare assetIds={selectedAssets} />);
  };

  const handleShareAllAssets = () => {
    const allAssets = collection?.data?.assets ?? [];
    showModal(<AddAssetsToShare assetIds={allAssets} />);
  };

  const handleCampaignLinksSelected = () => {
    showModal(<CampaignLinksModal mode="owner" assetIds={selectedAssets} />);
  };

  const handleCampaignLinksCollection = () => {
    showModal(<CampaignLinksModal mode="owner" collectionId={collectionId} />);
  };

  const handleOnClickAction = (action: string) => {
    switch (action) {
      case PAGE_ACTIONS.REMOVE_FROM_COLLECTION:
        return handleRemoveSelectedAssets();
      case PAGE_ACTIONS.SHARE_SELECTED:
        return handleShareSelectedAssets();
      case PAGE_ACTIONS.CAMPAIGN_LINKS_SELECTED:
        return handleCampaignLinksSelected();
      case PAGE_ACTIONS.REPROCESS_SELECTED:
        return showModal(
          <ReprocessModal
            assetIds={selectedAssets}
            contentTypes={selectedAssets.map((assetId) => extendedResult[assetId]?.content_type)}
          />,
        );
    }
  };

  const handleDuplicateCollection = () =>
    showModal(
      <ConfirmDuplicateCollectionModal
        title={`Duplicate collection ${collection.data.name}`}
        sourceId={collectionId}
        name={`${collection.data.name} (copy)`}
        description={collection.data.description}
        groupAccess={collection.data.group_access}
      />,
    );

  return (
    <><BoardingTour title='Collection Page' description='This page shows a single collection with assets that is being associated with' path='/collection' uniqueId='COLLECTION' />
    <div className={styles.pageWrapper}>
      <section className={styles.collectionHeader}>
        <h1 >
          <span className={styles.pageTitle}>Collection</span>
          <span className={styles.collectionName}>{collection?.data?.name && (collection?.data?.name.length>40?collection?.data?.name.slice(0,40)+'...':collection?.data?.name)}</span>

        </h1>
        {collection?.data?.description && (
          <p className={styles.collectionDescription} >{collection?.data?.description}</p>
        )}

      </section>

      {hasFilters && (
        <div className={styles.filterChips}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={styles.filterChip}
              onClick={() => removeFilter(chip.key)}
              title="Remove filter"
            >
              <span>{chip.label}</span>
              <X />
            </button>
          ))}
          <Button size="small" mode="tertiary" onClick={clearFilters}>
            Clear all
          </Button>
        </div>
      )}

      <PageTitle

        title="Assets"
        counter={totalMatching}
        midContainer={
          <div className={styles.buttonGroup}>
            <Button
              size="small"
              mode="tertiary"
              icon={<Copy />}
              onClick={handleCopyLink}
              disabled={!viewUrl}
            >
              Copy link
            </Button>
            {!isUser && (
              <Button
                size="small"
                mode="tertiary"
                icon={<Checks />}
                onClick={handleSendForReview}
                disabled={!collection?.data?.assets?.length}
              >
                Send for review
              </Button>
            )}
            {!isUser && (
              <Button
                size="small"
                mode="tertiary"
                icon={<ShareNetwork />}
                onClick={handleShareAllAssets}
                disabled={!collection?.data?.assets?.length}
              >
                Share
              </Button>
            )}
            {!isUser && (
              <Button
                size="small"
                mode="tertiary"
                icon={<AssetGetLink />}
                onClick={handleCampaignLinksCollection}
                disabled={!collection?.data?.assets?.length || !deliveryInsightsEnabled}
              >
                Campaign links
              </Button>
            )}
            <Button size="small" mode="tertiary" icon={<Copy />} onClick={handleDuplicateCollection}>
              Duplicate
            </Button>
            <Button
              size="small"
              mode="tertiary"
              icon={<Pencil />}
              onClick={showEditCollectionModal}
              disabled={userEmail !== collection?.data?.user_email}
            >
              Edit
            </Button>
          </div>
        }
        rightContainer={
          <Button
            size="small"
            icon={<FileArrowDown />}
            onClick={handleDownloadCollection}
            loading={isPreparingDownload}
            disabled={isPreparingDownload || !collection?.data?.assets.length}
          >
            Download All
          </Button>
        }
      />
      <p className={styles.lastUpdate}>
        Created by {userEmail === collection?.data?.user_email ? 'You' : collection?.data?.user_email}, updated{' '}
        {moment(collection?.data?.last_update).fromNow()}
      </p>

      <div className={styles.pageActions}>
        {displayedAssets.length > 0 && (
          <BatchActions
          basket={false}
          showSelectAll={!isUser && displayedAssets.length > 0}
          onSelectAll={handleSelectAllAssets}
          pageActions={!isUser ? {
            options: [
              {
                key: PAGE_ACTIONS.SHARE_SELECTED,
                value: 'Share Selected',
                icon: <ShareNetwork />,
                optionDisabled: selectedAssets.length === 0,
              },
              {
                key: PAGE_ACTIONS.CAMPAIGN_LINKS_SELECTED,
                value: 'Get campaign links',
                icon: <AssetGetLink />,
                optionDisabled: selectedAssets.length === 0 || !deliveryInsightsEnabled,
              },
              ...(canReprocess ? [{
                key: PAGE_ACTIONS.REPROCESS_SELECTED,
                value: 'Reprocess…',
                icon: <ArrowsClockwise />,
                optionDisabled:
                  selectedAssets.length === 0 || selectedAssets.length > MAX_REPROCESS_ASSETS,
              }] : []),
              {
                key: PAGE_ACTIONS.REMOVE_FROM_COLLECTION,
                value: 'Remove from collection',
                icon: <Trash />,
                optionDisabled: selectedAssets.length === 0,
              },
            ],
            onClick: handleOnClickAction,
          } : undefined}
        />
        )}
        {displayedAssets.length > 0 && <LayoutViewSwitcher />}
      </div>

      <LayoutView
        data={displayedAssets}
        isLoading={isSearchLoading}
        emptyDataMessage="No assets found"
        classNames={{
          cardView: 'listingGridWrapper',
        }}
        cardView={(assetId: string) => (
          <ListingCard
            key={assetId}
            id={assetId}
            siblings={displayedAssets}
            imageUrl={getThumbnailUrl({
              width: 300,
              height: 300,
              id: assetId,
              jwt: accessToken,
              version: thumbnailVersion(extendedResult[assetId]),
            })}
            name={extendedResult[assetId]?.name}
            size={extendedResult[assetId]?.size}
            contentType={extendedResult[assetId]?.content_type}
            trashed={extendedResult[assetId]?.trashed}
            status={extendedResult[assetId]?.status}
            onSelectChange={() => handleOnAssetSelectChange(assetId)}
            isSelected={selectedAssets.includes(assetId)}
            isGenerated={!!extendedResult[assetId]?.data?._filespin_original_asset_id}
            versions={extendedResult[assetId]?.versions}
            originalAssetId={extendedResult[assetId]?.data?._filespin_original_asset_id}
            error={extendedResult[assetId]?.errors}
          />
        )}
        listView={(assetId: string) => (
          <ListingRow
            key={assetId}
            id={assetId}
            siblings={displayedAssets}
            imageUrl={getThumbnailUrl({
              width: 100,
              height: 100,
              id: assetId,
              jwt: accessToken,
              version: thumbnailVersion(extendedResult[assetId]),
            })}
            name={extendedResult[assetId]?.name}
            size={extendedResult[assetId]?.size}
            uploadTime={extendedResult[assetId]?.upload_time}
            contentType={extendedResult[assetId]?.content_type}
            trashed={extendedResult[assetId]?.trashed}
            status={extendedResult[assetId]?.status}
            onSelectChange={() => handleOnAssetSelectChange(assetId)}
            isSelected={selectedAssets.includes(assetId)}
            isGenerated={!!extendedResult[assetId]?.data?._filespin_original_asset_id}
            versions={extendedResult[assetId]?.versions}
            originalAssetId={extendedResult[assetId]?.data?._filespin_original_asset_id}
            error={extendedResult[assetId]?.errors}
          />
        )}
      />

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            size="small"
            mode="tertiary"
            icon={<CaretLeft />}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className={styles.paginationInfo}>
            Page {page} of {totalPages}
          </span>
          <Button
            size="small"
            mode="tertiary"
            icon={<CaretRight />}
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
    </>
  );
};

export default Collection;
